import { config } from '@ps/config/client';
import { auth } from '@ps/functions/firebase/auth';

import type {
  SetupSubscriptionConfigPayload,
  UpdateSubscriptionConfigPayload,
} from '../types';

/**
 * Server-side subscription configuration writers. 1:1 with legacy
 * `flows/bio/pages/subscription-configuration/functions/subscriptionApis.ts`.
 *
 * Endpoints live on the AWS creator service:
 *   - POST  /setupSubscriptionConfig
 *   - PUT   /updateCreatorSubscriptionConfig
 *
 * Auth: Firebase ID token in `Authorization` header. The legacy
 * code used `auth.currentUser.getIdToken()` (no `true` force-
 * refresh); we match that to keep the call cost identical.
 *
 * No axios — the rest of next's subscription slice is on `fetch`,
 * so we stay consistent.
 */

async function authHeader() {
  const token = await auth().currentUser?.getIdToken();
  if (!token) throw new Error('User is not authenticated');
  return token;
}

/** Thrown when a subscription config write is rejected with a
 *  validation reason (422) — e.g. the price-change-lockout ("can't
 *  change price again until the current billing cycle ends / one
 *  month since the last change"). Carries the backend's message
 *  and, when applicable, the epoch-seconds timestamp the creator
 *  can next change the price at. 1:1 with legacy
 *  `useSubscriptionErrorHandler`'s `e.response.data` read. */
export class SubscriptionConfigApiError extends Error {
  constructor(
    readonly nextAllowedActivationDate: number | undefined,
    message: string,
  ) {
    super(message);
    this.name = 'SubscriptionConfigApiError';
  }
}

async function throwForFailedResponse(
  response: Response,
  fallbackMessage: string,
): Promise<never> {
  if (response.status === 422) {
    const body = (await response.json().catch(() => null)) as {
      message?: string;
      nextAllowedActivationDate?: number;
    } | null;
    if (body?.message) {
      throw new SubscriptionConfigApiError(
        body.nextAllowedActivationDate,
        body.message,
      );
    }
  }
  throw new Error(`${fallbackMessage}: ${response.status}`);
}

export async function setupSubscriptionConfig(
  payload: SetupSubscriptionConfigPayload,
): Promise<unknown> {
  const token = await authHeader();
  const response = await fetch(
    `${config.awsCreatorApiBaseUrl}/setupSubscriptionConfig`,
    {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    await throwForFailedResponse(response, 'setupSubscriptionConfig failed');
  }
  return response.json();
}

export async function updateSubscriptionConfig(
  payload: UpdateSubscriptionConfigPayload,
): Promise<unknown> {
  const token = await authHeader();
  const response = await fetch(
    `${config.awsCreatorApiBaseUrl}/updateCreatorSubscriptionConfig`,
    {
      method: 'PUT',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    await throwForFailedResponse(response, 'updateSubscriptionConfig failed');
  }
  return response.json();
}
